import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Section } from '../styles/CommonElements';
import Newsletter from '../components/Newsletter';
import SEO from '../components/SEO';

// Hero Section styling
const HeroSection = styled.div`
  padding: 5rem 0 3rem;
  position: relative;
  overflow: hidden;
  background: #f9f9f9;
  margin-top: -1rem;
  width: 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--primary-color);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const HeroTitleArea = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.75rem;
    left: 0;
    width: 80px;
    height: 4px;
    background: var(--primary-color);
    border-radius: 2px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.2rem;
  font-weight: 800;
  color: #2a3b4c;
  line-height: 1.2;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 2.6rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: #546677;
  margin-bottom: 1.2rem;
  font-weight: 400;
  max-width: 800px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Blog content styling
const BlogSection = styled(Section)`
  background-color: #fff;
  padding: 5rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 0;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 1.5rem;
  }
`;

const BlogCard = styled(Link)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: var(--primary-color);
  color: white;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
  align-self: flex-start;
`;

const CardTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #2a3b4c;
  margin-bottom: 0.75rem;
  line-height: 1.3;
`;

const CardExcerpt = styled.p`
  font-size: 1rem;
  color: #546677;
  line-height: 1.6;
  margin-bottom: 1rem;
  flex: 1;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  color: #888;
  font-size: 0.85rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;

  span {
    white-space: nowrap;
  }
`;

const ReadMore = styled.span`
  color: var(--primary-color);
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  margin-bottom: 1rem;
  
  &::after {
    content: '→';
    transition: transform 0.2s ease;
  }
  
  ${BlogCard}:hover &::after {
    transform: translateX(4px);
  }
`;

// Static blog posts data
const blogPosts = [
  {
    slug: 'third-party-delivery-integration-real-cost',
    title: 'The Real Cost of Third-Party Delivery Integration for Multi-Location Restaurants',
    excerpt: 'Third-party delivery marketplaces promise incremental revenue, but the technical and operational costs of integration are rarely discussed. Here\'s what multi-location QSR operators actually face—and how to do it right.',
    category: 'Delivery & Integration',
    author: 'Kerry Rayman',
    date: 'January 28, 2026',
    readTime: '10 min read'
  },
  {
    slug: 'vertically-integrated-qsr-order-processing',
    title: 'Why Vertically Integrated QSR Order Processing Suites Are the Future of Restaurant Technology',
    excerpt: 'Discover how unified order processing platforms that own the entire stack—from customer-facing ordering through kitchen production and delivery dispatch—are transforming quick-service restaurant operations.',
    category: 'POS Systems',
    author: 'Kerry Rayman',
    date: 'January 27, 2026',
    readTime: '12 min read'
  }
];

const BlogListPage: React.FC = () => {
  return (
    <main>
      <SEO 
        title="Blog | Software Development Insights & Industry News"
        description="Stay up to date with the latest software development trends, technology insights, and business innovation strategies from Uptrend Systems' expert team."
        keywords="software development blog, technology insights, business software solutions, custom software articles, digital transformation, tech industry news"
        canonicalUrl="/blog"
        pageType="blog"
      />
      
      <HeroSection>
        <div className="container font--jakarta">
          <HeroContent>
            <HeroTitleArea>
              <HeroTitle>Software Development Insights</HeroTitle>
            </HeroTitleArea>
            <HeroDescription>
              Explore our latest articles on custom software development, business technology, 
              and digital transformation. Our team shares expert insights to help you leverage 
              technology for business growth.
            </HeroDescription>
          </HeroContent>
        </div>
      </HeroSection>
      
      <BlogSection>
        <BlogGrid className="font--jakarta">
          {blogPosts.map((post) => (
            <BlogCard key={post.slug} to={`/blog/${post.slug}`}>
              <CardContent>
                <CategoryBadge>{post.category}</CategoryBadge>
                <CardTitle>{post.title}</CardTitle>
                <CardExcerpt>{post.excerpt}</CardExcerpt>
                <ReadMore>Read article</ReadMore>
                <CardMeta>
                  <span>{post.author}</span>
                  <span>•</span>
                  <span>{post.date}</span>
                  <span>•</span>
                  <span>{post.readTime}</span>
                </CardMeta>
              </CardContent>
            </BlogCard>
          ))}
        </BlogGrid>
      </BlogSection>
      
      <Newsletter />
    </main>
  );
};

export default BlogListPage;
